import { classNames, h } from "omi";
import isFunction from "lodash-es/isFunction";

import { IconName } from "@/components/atoms/nw-icon/icons";
import { NWIcon, NWTooltip } from "@/components/atoms";

type ToolbarItemBase = {
  shouldShow?: () => boolean;
  fontWeight?: string;
};

type ToolbarLink = ToolbarItemBase & {
  type: "link";
  name: string;
  icon: IconName;
  href: string;
  onClick?: () => void;
  withTooltip?: boolean;
};

type ToolbarButton = ToolbarItemBase & {
  type: "button";
  color: "gray" | "brand" | "gold" | "red";
  name: string;
  icon?: IconName;
  iconOnly?: boolean;
  semibold?: boolean;
  onClick: () => void;
  active: boolean | (() => boolean);
  tooltipText?: string;
  withTooltip?: boolean;
};

type ToolbarDivider = ToolbarItemBase & {
  type: "divider";
};

export type ToolbarItem = ToolbarLink | ToolbarButton | ToolbarDivider;

export function ToolbarItem(props: ToolbarItem) {
  const classes = "flex items-center p-3 space-x-2 text-primary rounded-lg transition-all duration-200";

  if (props.shouldShow && !props.shouldShow()) {
    return null;
  }

  if (props.type === "divider") {
    return <div class="h-11 border-r border-gray-200 w-1"></div>;
  }

  if (props.type === "link") {
    if (props.withTooltip !== false) {
      return (
        <NWTooltip placement="top" content={props.name} delay={[400, 0]}>
          <a
            href={(props as ToolbarLink).href}
            target="_blank"
            class={classNames(classes, "hover:bg-brand-100")}
            onClick={(props as ToolbarLink).onClick}
          >
            <NWIcon name={props.icon} width={24} height={24} />
            <span className={classNames(props.fontWeight ?? "font-bold", "hidden sm:inline")}>{props.name}</span>
          </a>
        </NWTooltip>
      );
    }
    return (
      <a
        href={(props as ToolbarLink).href}
        target="_blank"
        class={classNames(classes, "hover:bg-brand-100")}
        onClick={(props as ToolbarLink).onClick}
      >
        <NWIcon name={props.icon} width={24} height={24} />
        <span className={classNames(props.fontWeight ?? "font-bold", "hidden sm:inline")}>{props.name}</span>
      </a>
    );
  }

  const isActive = isFunction(props.active) ? props.active() : props.active;
  if (props.withTooltip !== false) {
    return (
      <NWTooltip
        placement="top"
        content={props.tooltipText ?? props.name}
        delay={[400, 0]}
        shouldShow={props.withTooltip ?? true}
      >
        <button
          onClick={(props as ToolbarButton).onClick}
          class={classNames(
            classes,
            { "bg-brand-100 rounded-lg": isActive },
            {
              "bg-leather hover:bg-brand-700": props.color === "brand",
              "hover:bg-brand-100": props.color !== "brand",
            },
          )}
        >
          <div
            class={classNames(
              "shrink-0 transition-all p-0.5 flex items-center space-x-1.5",
              { "text-leather": isActive },
              { "text-white": props.color === "brand" },
              { "text-warning-400": props.color === "gold" },
              { "text-error-400": props.color === "red" },
            )}
          >
            {props.icon && <NWIcon name={props.icon} width={20} height={20} />}
            {!props.iconOnly && (
              <span className={classNames(props.fontWeight ?? "font-bold", "hidden sm:inline")}>{props.name}</span>
            )}
          </div>
        </button>
      </NWTooltip>
    );
  }
  return (
    <button
      onClick={(props as ToolbarButton).onClick}
      class={classNames(
        classes,
        { "bg-brand-100 rounded-lg": isActive },
        {
          "bg-leather hover:bg-brand-700": props.color === "brand",
          "hover:bg-brand-100": props.color !== "brand",
        },
      )}
    >
      <div
        class={classNames(
          "shrink-0 transition-all p-0.5 flex items-center space-x-1.5",
          { "text-leather": isActive },
          { "text-white": props.color === "brand" },
          { "text-warning-400": props.color === "gold" },
          { "text-error-400": props.color === "red" },
        )}
      >
        {props.icon && <NWIcon name={props.icon} width={20} height={20} />}
        {!props.iconOnly && (
          <span className={classNames(props.fontWeight ?? "font-bold", "hidden sm:inline")}>{props.name}</span>
        )}
      </div>
    </button>
  );
}
